import { motion } from 'framer-motion'
import { Users, Link, BarChart3, Shield, TrendingUp, Activity, Database, Clock } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import toast from 'react-hot-toast'
import { adminApiService } from '../../../shared/services/adminApi'
import { AdminHeader, SystemStatusCard } from '../../../shared/components'
import type { DashboardStats, SystemHealth } from '../../../shared/services/adminApi'

export default function AdminDashboardPage() {
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [stats, setStats] = useState<DashboardStats[]>([])
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null)
  const [securityAlerts, setSecurityAlerts] = useState<any[]>([])
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  useEffect(() => {
    loadDashboardData()
    
    // Set up interval for real-time security updates (every 30 seconds)
    const securityUpdateInterval = setInterval(async () => {
      try {
        const securityData = await adminApiService.getSecurityAlerts()
        setSecurityAlerts(securityData)
      } catch (error) {
        console.error('Failed to update security alerts:', error)
      }
    }, 30000) // 30 seconds

    // Clean up interval on component unmount
    return () => {
      clearInterval(securityUpdateInterval)
    }
  }, [])

  const loadDashboardData = async () => {
    try {
      setIsLoading(true)
      
      // Load dashboard stats, system health, and security alerts in parallel
      const [statsData, healthData, securityData] = await Promise.all([
        adminApiService.getDashboardStats(),
        adminApiService.getSystemHealth(),
        adminApiService.getSecurityAlerts().catch(() => []) // Graceful fallback for security alerts
      ])

      setStats(statsData)
      setSystemHealth(healthData)
      setSecurityAlerts(securityData)
      setLastUpdated(new Date())
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
      toast.error('Failed to load dashboard data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true)
      await loadDashboardData()
      toast.success('Dashboard data refreshed')
    } catch (error) {
      console.error('Failed to refresh data:', error)
      toast.error('Failed to refresh data')
    } finally {
      setIsRefreshing(false)
    }
  }

  const getIconComponent = (iconName: string) => {
    const icons = {
      Users,
      Link,
      BarChart3,
      Shield,
      Activity,
      Database,
      Clock
    }
    return icons[iconName as keyof typeof icons] || Users
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Admin Header */}
      <AdminHeader 
        title="Admin Dashboard"
        subtitle="System overview and management controls"
        showRefresh={true}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        lastUpdated={lastUpdated}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* System Status Banner */}
        {systemHealth && (
          <SystemStatusCard 
            systemHealth={systemHealth}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
            className="mb-6"
          />
        )}

        {/* Stats Grid */}
        <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat, index) => {
            const IconComponent = getIconComponent(stat.icon)
            
            // Update Security Alerts with real-time data
            const displayStat = stat.name === 'Security Alerts' 
              ? {
                  ...stat,
                  value: securityAlerts.length.toString(),
                  todayCount: securityAlerts.filter(alert => {
                    const alertDate = new Date(alert.timestamp || alert.createdAt)
                    const today = new Date()
                    return alertDate.toDateString() === today.toDateString()
                  }).length
                }
              : stat
            
            return (
              <motion.div
                key={stat.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg"
              >
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <IconComponent 
                        className={`h-6 w-6 ${
                          stat.name === 'Security Alerts' 
                            ? 'text-red-500' 
                            : 'text-indigo-600'
                        }`} 
                      />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                          {displayStat.name}
                        </dt>
                        <dd className="text-lg font-medium text-gray-900 dark:text-white">
                          {displayStat.value}
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 px-5 py-3">
                  <div className="text-sm">
                    <span
                      className={`font-medium ${
                        displayStat.changeType === 'increase'
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {displayStat.change}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400"> from last month</span>
                    {displayStat.todayCount !== undefined && (
                      <span className="block text-xs text-gray-400 dark:text-gray-500">
                        Today: {displayStat.todayCount}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>



        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-8"
        >
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
            Quick Actions
          </h3>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <RouterLink 
              to="/users"
              className="relative group bg-white dark:bg-gray-800 p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500 rounded-lg shadow hover:shadow-md transition-shadow duration-200"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-indigo-50 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 ring-4 ring-white dark:ring-gray-800">
                  <Users className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-8">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  User Management
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Manage user accounts, permissions, and access levels.
                </p>
              </div>
            </RouterLink>

            <RouterLink 
              to="/analytics"
              className="relative group bg-white dark:bg-gray-800 p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500 rounded-lg shadow hover:shadow-md transition-shadow duration-200"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-green-50 dark:bg-green-900/50 text-green-600 dark:text-green-400 ring-4 ring-white dark:ring-gray-800">
                  <TrendingUp className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-8">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Analytics
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  View detailed system analytics and performance metrics.
                </p>
              </div>
            </RouterLink>

            <RouterLink 
              to="/security"
              className="relative group bg-white dark:bg-gray-800 p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-indigo-500 rounded-lg shadow hover:shadow-md transition-shadow duration-200"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-red-50 dark:bg-red-900/50 text-red-600 dark:text-red-400 ring-4 ring-white dark:ring-gray-800">
                  <Shield className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-8">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Security Settings
                </h3>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  Configure security policies and monitor threats.
                </p>
              </div>
            </RouterLink>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
